import React, { useState, useEffect } from "react";
import CommonModal from "@/components/modal/CommonModal";
import Select from "react-select";
import { FiCheck, FiSearch } from "react-icons/fi";
import { useAuth } from "@/contexts/AuthContext";
import { currencies as allCurrencies, getCurrencyFlag } from "@/utils/currencies";

const DEFAULT_CODES = ["USD", "GBP", "EUR", "ILS", "AUD", "CAD", "ZAR"];
const defaultCurrencies = allCurrencies.filter((c) => DEFAULT_CODES.includes(c.code));

interface CurrencyModalProps {
  externalOpen?: boolean;
  onExternalClose?: () => void;
}

const CurrencyModal: React.FC<CurrencyModalProps> = ({ externalOpen, onExternalClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser, updateCurrency, loading: authLoading } = useAuth();

  // `label` must be plain text — react-select searches it.
  const countries = allCurrencies.map((c) => ({
    label: `${c.country} ${c.code} ${c.symbol}`,
    value: c.code.toLowerCase(),
    currency: c,
  }));

  // Show modal only if user doesn't have a currency set and auth is not loading
  useEffect(() => {
    if (!authLoading && currentUser && !currentUser.currency) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [currentUser, authLoading]);

  const modalOpen = isOpen || !!externalOpen;

  const handleCurrencySelect = async (currencyCode: string) => {
    try {
      setIsLoading(true);
      await updateCurrency(currencyCode);
      setIsOpen(false);
      onExternalClose?.();
    } catch (error) {
      console.error("Failed to save currency:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CommonModal
      isOpen={modalOpen}
      onOpenChange={(open) => { if (!open) { setIsOpen(false); onExternalClose?.(); } }}
      title="Select Currency"
      size="3xl"
      closeButton={false}
    >
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600">Saving your preference...</p>
          </div>
        </div>
      )}

      <div>
        <Select
          options={countries}
          isSearchable
          formatOptionLabel={({ currency: c }) => (
            <div className="flex items-center gap-2 cursor-pointer">
              <img
                className="h-6 w-6 rounded-full object-cover"
                src={getCurrencyFlag(c.code)}
                alt={c.countryCode}
              />
              <span>
                {c.country}{" "}
                <span className="text-gray-500">
                  ({c.code} {c.symbol})
                </span>
              </span>
            </div>
          )}
          placeholder="Search country or currency name"
          onChange={(newValue) => {
            if (newValue && !isLoading) {
              handleCurrencySelect(newValue.value);
            }
          }}
          isDisabled={isLoading}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          components={{
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
            Control: ({ children, innerRef, innerProps }) => (
              <div
                ref={innerRef}
                {...innerProps}
                className={` bg-white shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]  border px-3.5 py-1 ps-8 border-gray-300 rounded-lg  min-w-[300px] hover:border-primary relative flex items-center cursor-pointer focus:border-primary
                             `}
              >
                <FiSearch className="absolute left-3 text-gray-500" size={20} />
                {children}
              </div>
            ),
            Option: ({ children, isSelected, innerRef, innerProps }) => (
              <div
                ref={innerRef}
                {...innerProps}
                className={`flex justify-between items-center p-2 hover:bg-gray-100 ${
                  isSelected ? "bg-gray-100" : "bg-white"
                }`}
              >
                <span>{children}</span>
                {isSelected && (
                  <FiCheck className="text-gray-700 ml-2" size={18} />
                )}
              </div>
            ),
          }}
          classNames={{
            input: () => `text-gray-700 !p-0 `,
            placeholder: () => `!text-gray-500 `,
            menuPortal: () => `!z-[9999]`,
            menu: () =>
              `!rounded-lg !shadow-customShadow !border !border-gray-100 !bg-white`,
          }}
        />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4">
        {defaultCurrencies.map((currency) => (
          <div
            key={currency.code}
            onClick={() =>
              !isLoading && handleCurrencySelect(currency.code.toLowerCase())
            }
            className={`flex items-center border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <img
              src={getCurrencyFlag(currency.code)}
              alt={`${currency.country} flag`}
              className="w-8 h-8 rounded object-cover"
            />
            <div className="ml-3">
              <span className="text-sm font-medium text-gray-700">
                {currency.country}
              </span>
              <span className="text-sm font-normal text-gray-500">
                {" "}
                ({currency.code} {currency.symbol})
              </span>
            </div>
          </div>
        ))}
      </div>
    </CommonModal>
  );
};

export default CurrencyModal;
